import React, { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';

// ─── Types ────────────────────────────────────────────────────────────────────

type DbEngine = 'mongo' | 'mysql' | 'redis';

interface FieldDef {
  name: string;
  type: string;
  description: string;
  sample?: string;
  note?: string;
}

interface CollectionDef {
  name: string;
  engine: DbEngine;
  description: string;
  specRef?: string;
  isNew?: boolean;
  fields: FieldDef[];
  indexes?: string[];
  notes?: string[];
  todo?: string[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ENGINE_COLORS: Record<DbEngine, { bg: string; text: string; label: string }> = {
  mongo: { bg: 'rgba(77, 171, 77, 0.12)', text: '#4dab4d', label: 'MongoDB' },
  mysql: { bg: 'rgba(0, 136, 204, 0.12)', text: '#0088cc', label: 'MySQL' },
  redis: { bg: 'rgba(220, 64, 48, 0.12)', text: '#dc4030', label: 'Redis' },
};

// ─── Data ─────────────────────────────────────────────────────────────────────

const collections: CollectionDef[] = [
  // ── MongoDB ──────────────────────────────────────────────────────────────
  {
    name: 'MainDB.ProductEmbeddingCrops',
    engine: 'mongo',
    description:
      'Crop metadata — bounding boxes, UPCs, image references, orientation, and (new) design linkage. Only front-orientation crops receive a design_id.',
    specRef: '§5.1.1',
    fields: [
      { name: '_id', type: 'ObjectId', description: 'Crop document ID', sample: '696689757c25004085e1cc24' },
      { name: 'store_id', type: 'int', description: 'Store identifier', sample: '1296' },
      { name: 'chain_id', type: 'int', description: 'Chain identifier', sample: '50' },
      { name: 'barcode', type: 'string', description: 'Raw scanned/store barcode string. Forge treats this as the identity source.', sample: '"4056489441786"' },
      { name: 'normalized_upc', type: 'string', description: 'UPC-form companion key used to join into product metadata. Preserved for valid same-item moves.', sample: '"04056489441762"' },
      { name: 'product_name', type: 'string', description: 'Product display name', sample: '"Lessive concentrée"' },
      { name: 'is_synthetic', type: 'bool', description: 'Whether crop is synthetic', sample: 'false' },
      { name: 'source', type: 'string', description: 'Crop source (scan, forge, control, other)', sample: '"forge"' },
      { name: 'image_id', type: 'int', description: 'Source image ID', sample: '3514879680' },
      { name: 'image_resolution', type: 'string', description: 'Source image resolution', sample: '"8mp"' },
      { name: 'coords', type: 'Object', description: 'Bounding box coordinates (x, y, w, h)', sample: '{ x: 739, y: 0, w: 218, h: 487 }' },
      { name: 'viz_url', type: 'string', description: 'CDN path to the crop visualization', sample: '"/focal-shelf-images-eu/lidl_fr_4289/4C7525E2E330/2026-01-13/..."' },
      { name: 'orientation', type: 'string', description: 'Crop orientation (front, top, side, etc.)', sample: '"front"' },
      { name: 'classification_type', type: 'string', description: 'Product classification type', sample: '"product"' },
      { name: 'is_active', type: 'bool', description: 'Soft-delete flag', sample: 'true' },
      { name: 'created_by', type: 'string', description: 'Creator email', sample: '"andre.cortereal@focal.systems"' },
      { name: 'created_at', type: 'datetime', description: 'Creation timestamp', sample: '2026-01-13T18:05:41.438+00:00' },
      { name: 'transformation_matrix', type: 'string', description: 'Affine transformation matrix (JSON string)', sample: '"[[1.331, 0.040, -739.59], ...]"' },
      {
        name: 'design_id',
        type: 'string (UUID)',
        description: 'Design this crop belongs to. Only set for front-orientation crops.',
        sample: '"a1b2c3d4-..."',
        note: 'Nullable. Non-front crops always null.',
      },
    ],
    indexes: [
      '(chain_id, created_at, normalized_upc)',
      '(chain_id, is_active, created_at, normalized_upc)',
      '(store_id, normalized_upc, is_active)',
      '(chain_id, is_active, item_number)',
      '(chain_id, design_id)',
      '(chain_id, barcode)',
    ],
    notes: [
      'Only orientation="front" crops receive a design_id.',
      'No representative_crop_id flag — representative tracking lives in Mongo ForgeDesigns.representative_crop_id.',
      'Forge may set source="forge" when a Forge action rewrites barcode or normalized_upc.',
      'barcode is the Forge identity source; normalized_upc is the UPC-form companion used for product lookups.',
    ],
  },
  {
    name: 'MainDB.ProductEmbeddings',
    engine: 'mongo',
    description:
      'Embedding vectors and metadata for nearest-neighbor search. Links to a crop via product_embedding_crop_id.',
    specRef: '§5.1.2',
    fields: [
      { name: '_id', type: 'ObjectId', description: 'Embedding document ID', sample: '6985a816679a66016f00a12b' },
      { name: 'is_active', type: 'bool', description: 'Soft-delete flag', sample: 'false' },
      { name: 'product_master_upc', type: 'bool', description: 'Whether this is a master UPC embedding', sample: 'true' },
      { name: 'normalized_upc', type: 'string', description: 'Canonical UPC key', sample: '"05000237120149"' },
      { name: 'chain_id', type: 'int', description: 'Chain identifier', sample: '33' },
      { name: 'visual_embedding_model_version', type: 'string', description: 'Model version that generated this embedding', sample: '"0.0.4"' },
      { name: 'created_by', type: 'string', description: 'Creator email', sample: '"piotr.tatarczyk@focal.systems"' },
      { name: 'product_embedding_crop_id', type: 'ObjectId', description: 'Link to the parent crop document', sample: '689c69d53610625ebda385c5' },
      { name: 'store_id', type: 'int', description: 'Store identifier', sample: '1260' },
      { name: 'embedding', type: 'Array (512)', description: '512-dimensional float vector for similarity search', sample: '[0.0234, -0.1456, ...]' },
      { name: 'ocr', type: 'Object', description: 'OCR extraction results from the crop image', sample: '{ text: "...", confidence: 0.95 }' },
      { name: 'created_at', type: 'datetime', description: 'Creation timestamp', sample: '2026-02-06T08:36:38.880+00:00' },
      {
        name: 'design_id',
        type: 'string (UUID)',
        description: 'Denormalized from crop. Auto-inherited when new model version embeddings are created.',
        sample: '"a1b2c3d4-..."',
        note: 'Nullable. Front crops only.',
      },
    ],
    notes: [
      'No changes to $vectorSearch index in v1.',
      'When embedding model changes (~every 2 months), new embeddings auto-inherit design_id from parent crop.',
    ],
  },

  // ── MySQL ────────────────────────────────────────────────────────────────
  {
    name: 'MainDB.ForgeDesigns',
    engine: 'mongo',
    description:
      'Mongo-backed design metadata and identity. Each design is chain-scoped with a persisted barcode, independent media metadata, and a representative crop pointer.',
    specRef: '§5.1.3',
    isNew: true,
    fields: [
      { name: '_id', type: 'ObjectId', description: 'Mongo document ID and canonical persisted design identifier used by design APIs' },
      { name: 'chain_id', type: 'int', description: 'Chain identifier (immutable)' },
      { name: 'representative_crop_id', type: 'string nullable', description: 'Mongo ObjectId of the representative crop' },
      { name: 'barcode_status', type: 'enum', description: 'Barcode verification status', sample: '"unconfirmed"' },
      { name: 'front_image_url', type: 'string nullable', description: 'Verified front image URL stored on the design' },
      { name: 'back_image_url', type: 'string nullable', description: 'Verified back image URL stored on the design' },
      { name: 'barcode_image_url', type: 'string nullable', description: 'Verified barcode image URL stored on the design' },
      { name: 'barcode', type: 'string nullable', description: 'Persisted barcode value stored on the design' },
      { name: 'created_at', type: 'datetime', description: 'Creation timestamp' },
      { name: 'updated_at', type: 'datetime', description: 'Last update timestamp' },
      { name: 'updated_by', type: 'string', description: 'Email of last updater' },
    ],
    indexes: ['UNIQUE (_id) (implicit)', '(chain_id, barcode)', '(chain_id, created_at)'],
    notes: [
      'This is the single live source of truth for designs.',
      'Crop membership is not stored here; it is derived from ProductEmbeddingCrops.design_id, whose value is the Forge design _id string.',
      'ForgeDesigns no longer stores item_number; item context is derived from active design crops or from the stored barcode for empty designs.',
      'Empty designs are deleted automatically unless they carry a persisted barcode.',
      'front_image_url/back_image_url/barcode_image_url/barcode are independent design metadata, not auto-derived from crops.',
    ],
    todo: [
      'Add sample row once ForgeDesigns has populated staging data.',
    ],
  },
  {
    name: 'forge_tasks',
    engine: 'mysql',
    description:
      'Task definitions, states, and lifecycle. Non-overlapping generation ensures no two active tasks share any entity.',
    specRef: '§5.2.3',
    isNew: true,
    fields: [
      { name: 'task_id', type: 'UUID PK', description: 'Task identifier' },
      { name: 'task_type', type: 'enum', description: 'DESIGN_REVIEW | DESIGN_CURATOR | UNCERTAIN_CLASSIFICATION | FD_CLASSIFICATION' },
      { name: 'queue_name', type: 'string', description: 'Queue this task belongs to' },
      { name: 'chain_id', type: 'int', description: 'Chain identifier' },
      { name: 'priority_class', type: 'enum', description: 'high | normal | low' },
      { name: 'status', type: 'enum', description: 'queued | claimed | submitted | skipped | rejected | superseded' },
      { name: 'payload_json', type: 'JSON', description: 'Task-type-specific input data (pre-computed candidates, highlights, etc.)' },
      { name: 'entity_refs_json', type: 'JSON', description: 'Minimal primary IDs for non-overlapping generation (see §6.8)' },
      { name: 'claimed_by', type: 'string nullable', description: 'Email of the labeler who claimed this task' },
      { name: 'claimed_at', type: 'datetime nullable', description: 'When the task was claimed' },
      { name: 'lease_expires_at', type: 'datetime nullable', description: 'Lease expiry (default 15 min). Abandoned on expiry, not re-enqueued.' },
      { name: 'is_gold', type: 'bool', description: 'Reserved for gold task logic (default: false, deferred)' },
      { name: 'gold_expected_answer_json', type: 'JSON nullable', description: 'Expected answers for gold tasks (reserved, deferred)' },
      { name: 'created_at', type: 'datetime', description: 'Creation timestamp' },
      { name: 'updated_at', type: 'datetime', description: 'Last update timestamp' },
    ],
    notes: [
      'rejected = submit-time precondition validation failed (rare).',
      'superseded = task was flushed during queue regeneration.',
      'No frozen_versions_json — non-overlapping generation eliminates staleness tracking.',
      'Abandoned tasks are recreated by the next regeneration cycle, not re-enqueued.',
    ],
    todo: [
      'Add sample row once task engine is running in staging.',
    ],
  },
  {
    name: 'forge_change_log',
    engine: 'mysql',
    description:
      'Append-only audit trail for all design/crop changes. 180-day TTL with weekly cron cleanup.',
    specRef: '§5.2.4',
    isNew: true,
    fields: [
      { name: 'change_id', type: 'UUID PK', description: 'Change entry identifier' },
      { name: 'entity_type', type: 'string', description: 'Entity type (design, crop, embedding)' },
      { name: 'entity_id', type: 'string', description: 'Entity identifier' },
      { name: 'action', type: 'string', description: 'Action performed (assign_design, merge, deactivate, create_design, etc.)' },
      { name: 'actor_id', type: 'string', description: 'Email of the actor' },
      { name: 'task_id', type: 'UUID nullable', description: 'FK to forge_tasks. NULL for admin operations (Master Label).' },
      { name: 'before_json', type: 'JSON', description: 'Snapshot of entity state before the change' },
      { name: 'after_json', type: 'JSON', description: 'Snapshot of entity state after the change' },
      { name: 'metadata_json', type: 'JSON nullable', description: 'Extra context (e.g., merge source/target details)' },
      { name: 'created_at', type: 'datetime', description: 'When the change was recorded' },
    ],
    notes: [
      'Append-only. 180-day TTL — weekly cron deletes older rows.',
      'Backend-only in v1. No frontend exposure.',
      'Every task submit writes one or more change_log entries.',
    ],
    todo: [
      'Add sample row showing a typical design_review change.',
      'Add sample row showing a merge change with before/after snapshots.',
    ],
  },
  {
    name: 'forge_labeler_assignments',
    engine: 'mysql',
    description:
      'Whitelist of which queues each labeler can access. Simple many-to-many join table.',
    specRef: '§5.2.5',
    isNew: true,
    fields: [
      { name: 'id', type: 'int PK', description: 'Auto-increment primary key' },
      { name: 'labeler_id', type: 'string', description: 'Labeler email' },
      { name: 'queue_name', type: 'string', description: 'Queue name (e.g., design_review, design_curator)' },
      { name: 'created_at', type: 'datetime', description: 'Creation timestamp' },
      { name: 'updated_at', type: 'datetime', description: 'Last update timestamp' },
    ],
    indexes: ['UNIQUE (labeler_id, queue_name)'],
    notes: [
      'No per-labeler chain filtering in v1.',
      'Admin-only queues (e.g., QA review) are achieved by only whitelisting admin users.',
    ],
    todo: [
      'Add sample rows once labeler assignment UI is functional.',
    ],
  },
  {
    name: 'forge_regen_log',
    engine: 'mysql',
    description:
      'Tracks regeneration cycles — when queues are regenerated, how many tasks were created/superseded, and performance.',
    specRef: '§5.2.7',
    isNew: true,
    fields: [
      { name: 'id', type: 'int PK', description: 'Auto-increment primary key' },
      { name: 'chain_id', type: 'int', description: 'Chain that was regenerated' },
      { name: 'trigger', type: 'enum', description: 'queue_empty | on_demand | scheduled' },
      { name: 'tasks_superseded', type: 'int', description: 'Count of queued tasks flushed' },
      { name: 'tasks_created_json', type: 'JSON', description: 'Breakdown by task type, e.g. {"design_review": 45, "design_curator": 12}' },
      { name: 'duration_ms', type: 'int', description: 'Duration of the regeneration cycle in milliseconds' },
      { name: 'created_at', type: 'datetime', description: 'When the regeneration ran' },
    ],
    notes: [
      '180-day TTL — weekly cron deletes older rows.',
    ],
    todo: [
      'Add sample row once regeneration service is running.',
    ],
  },
  {
    name: 'store_settings (forge_* keys)',
    engine: 'mysql',
    description:
      'Existing chain/store-level cascading config table. Forge uses forge_-prefixed keys for queue params, thresholds, and feature flags.',
    specRef: '§5.2.6',
    fields: [
      { name: 'forge_enabled', type: 'string', description: 'Master enable/disable for Forge (default: "true")' },
      { name: 'forge_enabled__{queue}', type: 'string', description: 'Enable/disable a specific queue (default: "true")' },
      { name: 'forge_max_tasks_per_regen__{queue}', type: 'string', description: 'Max tasks per regeneration run (default: "200")' },
      { name: 'forge_priority_class__{queue}', type: 'string', description: 'Chain priority in queue — high|normal|low (default: "normal")' },
      { name: 'forge_qa_sample_rate__{queue}', type: 'string', description: 'Fraction of submitted tasks sampled for QA (default: "0.0")' },
      { name: 'forge_detector_params__{queue}', type: 'string', description: 'JSON string with detector-specific thresholds (default: "{}")' },
      { name: 'forge_regen_lock_ttl_seconds', type: 'string', description: 'Distributed lock TTL for regeneration (default: "300")' },
      { name: 'forge_queue_stickiness_tasks', type: 'string', description: 'Tasks before re-evaluating queue assignment (default: "20")' },
      { name: 'forge_queue_stickiness_minutes', type: 'string', description: 'Minutes before re-evaluating queue assignment (default: "30")' },
    ],
    notes: [
      'Store-level overrides chain-level. Hardcoded defaults in code as fallback.',
      'Queue-specific keys use double underscore: forge_{param}__{queue_name}.',
      'Resolved via existing StoreSettingDao.get_by_store_id_name() / get_by_chain_id_name().',
    ],
  },

  // ── Redis ────────────────────────────────────────────────────────────────
  {
    name: 'Queue Lists',
    engine: 'redis',
    description:
      'Separate Redis lists per (queue_name, priority_class) backing queue ordering. Claim pops from high → normal → low.',
    specRef: '§6.2',
    fields: [
      { name: 'forge:queue:{queue_name}:{priority_class}', type: 'LIST', description: 'Ordered list of task_id strings. E.g., forge:queue:design_review:high' },
      { name: 'forge:regen_lock:{chain_id}', type: 'STRING', description: 'Distributed lock key for regeneration. TTL-based (default 300s).' },
    ],
    notes: [
      'Redis stores only task_id strings — full task data lives in MySQL.',
      'Claim pops from highest-priority non-empty list first.',
      'Regeneration flushes and rebuilds lists.',
    ],
  },
];

// ─── Components ───────────────────────────────────────────────────────────────

const EngineBadge: React.FC<{ engine: DbEngine }> = ({ engine }) => {
  const c = ENGINE_COLORS[engine];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        fontFamily: 'var(--font-mono)',
        background: c.bg,
        color: c.text,
      }}
    >
      {c.label}
    </span>
  );
};

const NewBadge: React.FC = () => (
  <span
    style={{
      display: 'inline-flex',
      padding: '2px 6px',
      borderRadius: 4,
      fontSize: 10,
      fontWeight: 700,
      fontFamily: 'var(--font-mono)',
      background: 'rgba(255, 170, 0, 0.15)',
      color: '#e6a000',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    }}
  >
    new
  </span>
);

const FieldRow: React.FC<{ field: FieldDef; isLast: boolean }> = ({ field, isLast }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: '180px 120px 1fr',
      gap: 12,
      padding: '8px 16px',
      borderBottom: isLast ? 'none' : '1px solid var(--divider)',
      fontSize: 12,
      alignItems: 'start',
    }}
  >
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontWeight: 600,
          color: field.note ? '#e6a000' : 'var(--text-primary)',
        }}
      >
        {field.name}
      </span>
      {field.note && (
        <span style={{ fontSize: 10, color: '#e6a000', fontStyle: 'italic' }}>{field.note}</span>
      )}
    </div>
    <span
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 11,
        color: 'var(--text-tertiary)',
      }}
    >
      {field.type}
    </span>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ color: 'var(--text-secondary)' }}>{field.description}</span>
      {field.sample && (
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: 'var(--text-tertiary)',
            opacity: 0.8,
          }}
        >
          {field.sample}
        </span>
      )}
    </div>
  </div>
);

const CollectionCard: React.FC<{ collection: CollectionDef; isExpanded: boolean; onToggle: () => void }> = ({
  collection,
  isExpanded,
  onToggle,
}) => (
  <div
    style={{
      background: 'var(--surface)',
      borderRadius: 8,
      border: '1px solid var(--border)',
      overflow: 'hidden',
    }}
  >
    {/* Header */}
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '14px 16px',
        cursor: 'pointer',
        userSelect: 'none',
      }}
      onClick={onToggle}
    >
      <span style={{ color: 'var(--text-secondary)', display: 'flex' }}>
        <StorageOutlinedIcon fontSize="small" />
      </span>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--text-primary)',
        }}
      >
        {collection.name}
      </span>
      <EngineBadge engine={collection.engine} />
      {collection.isNew && <NewBadge />}
      {collection.specRef && (
        <span
          style={{
            fontSize: 11,
            color: 'var(--text-tertiary)',
            fontFamily: 'var(--font-mono)',
          }}
        >
          {collection.specRef}
        </span>
      )}
      <span style={{ flex: 1 }} />
      <span
        style={{
          fontSize: 11,
          color: 'var(--text-tertiary)',
          fontFamily: 'var(--font-mono)',
        }}
      >
        {collection.fields.length} fields
      </span>
      {isExpanded ? (
        <ExpandLessIcon style={{ fontSize: 18, color: 'var(--text-tertiary)' }} />
      ) : (
        <ExpandMoreIcon style={{ fontSize: 18, color: 'var(--text-tertiary)' }} />
      )}
    </div>

    {isExpanded && (
      <>
        {/* Description */}
        <div
          style={{
            padding: '0 16px 12px',
            fontSize: 12,
            color: 'var(--text-tertiary)',
            borderBottom: '1px solid var(--divider)',
          }}
        >
          {collection.description}
        </div>

        {/* Field header */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '180px 120px 1fr',
            gap: 12,
            padding: '8px 16px',
            borderBottom: '1px solid var(--divider)',
            fontSize: 10,
            fontWeight: 600,
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          <span>Field</span>
          <span>Type</span>
          <span>Description / Sample</span>
        </div>

        {/* Fields */}
        {collection.fields.map((field, idx) => (
          <FieldRow key={field.name} field={field} isLast={idx === collection.fields.length - 1} />
        ))}

        {/* Indexes */}
        {collection.indexes && collection.indexes.length > 0 && (
          <div
            style={{
              padding: '10px 16px',
              borderTop: '1px solid var(--divider)',
              fontSize: 12,
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: 'var(--text-tertiary)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Indexes
            </span>
            <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {collection.indexes.map((idx) => (
                <span
                  key={idx}
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 11,
                    color: 'var(--text-secondary)',
                  }}
                >
                  {idx}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {collection.notes && collection.notes.length > 0 && (
          <div
            style={{
              padding: '10px 16px',
              borderTop: '1px solid var(--divider)',
              fontSize: 12,
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: 'var(--text-tertiary)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Notes
            </span>
            <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {collection.notes.map((note, i) => (
                <span key={i} style={{ color: 'var(--text-secondary)' }}>
                  • {note}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* TODOs */}
        {collection.todo && collection.todo.length > 0 && (
          <div
            style={{
              padding: '10px 16px',
              borderTop: '1px solid var(--divider)',
              fontSize: 12,
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: '#e6a000',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              TODO
            </span>
            <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {collection.todo.map((item, i) => (
                <span key={i} style={{ color: '#e6a000', fontStyle: 'italic' }}>
                  • {item}
                </span>
              ))}
            </div>
          </div>
        )}
      </>
    )}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

export const DatabasePage: React.FC = () => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggle = (name: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const expandAll = () => setExpandedIds(new Set(collections.map((c) => c.name)));
  const collapseAll = () => setExpandedIds(new Set());

  const mongoCollections = collections.filter((c) => c.engine === 'mongo');
  const mysqlCollections = collections.filter((c) => c.engine === 'mysql');
  const redisCollections = collections.filter((c) => c.engine === 'redis');

  const engineSections: { engine: DbEngine; items: CollectionDef[] }[] = [
    { engine: 'mongo', items: mongoCollections },
    { engine: 'mysql', items: mysqlCollections },
    { engine: 'redis', items: redisCollections },
  ];

  return (
    <div className="page-content" style={{ maxWidth: 960, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <StorageOutlinedIcon style={{ fontSize: 22, color: 'var(--text-secondary)' }} />
          <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
            Database Schema
          </h1>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-tertiary)', margin: 0, lineHeight: 1.5 }}>
          All databases affected by Forge — MongoDB collections (existing, with new fields), MySQL tables (new), and Redis key structures.
        </p>
      </div>

      {/* Summary bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: '10px 16px',
          marginBottom: 20,
          background: 'var(--surface)',
          borderRadius: 8,
          border: '1px solid var(--border)',
          fontSize: 12,
        }}
      >
        <span style={{ color: 'var(--text-tertiary)' }}>
          <strong style={{ color: 'var(--text-primary)' }}>{collections.length}</strong> collections/tables
        </span>
        <span style={{ color: 'var(--border)' }}>|</span>
        {engineSections.map(({ engine, items }) => (
          <span key={engine} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <EngineBadge engine={engine} />
            <span style={{ color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
              {items.length}
            </span>
          </span>
        ))}
        <span style={{ flex: 1 }} />
        <button
          onClick={expandAll}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-tertiary)',
            cursor: 'pointer',
            fontSize: 11,
            padding: '2px 6px',
          }}
        >
          Expand all
        </button>
        <button
          onClick={collapseAll}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-tertiary)',
            cursor: 'pointer',
            fontSize: 11,
            padding: '2px 6px',
          }}
        >
          Collapse all
        </button>
      </div>

      {/* Engine sections */}
      {engineSections.map(({ engine, items }) => (
        <div key={engine} style={{ marginBottom: 28 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 12,
              paddingBottom: 6,
              borderBottom: `2px solid ${ENGINE_COLORS[engine].text}20`,
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 700, color: ENGINE_COLORS[engine].text }}>
              {ENGINE_COLORS[engine].label}
            </span>
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
              {engine === 'mongo' && '— existing collections with new fields'}
              {engine === 'mysql' && '— new tables (forge_ prefix) + config in store_settings'}
              {engine === 'redis' && '— queue ordering & distributed locks'}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map((collection) => (
              <CollectionCard
                key={collection.name}
                collection={collection}
                isExpanded={expandedIds.has(collection.name)}
                onToggle={() => toggle(collection.name)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
